"use client";
import FactoidBar from "@/components/CourseContent/ContentRefresh/FactoidBar";
import { ProgramDetailContentProps } from "@/components/ProgramDetailLayout/types";
import InstructorList from "@/components/CourseContent/ContentRefresh/InstructorList";
import ProgramContent from "@/components/CourseContent/ContentOverview";
import WhatYoullLearn from "@/components/CourseContent/ContentRefresh/WhatYoullLearn";
import {
  fullWidthContainer,
  mainContent,
  mainContentSideRail,
  maxWidthContainer,
} from "@/components/CourseContent/styles/constants";
import styles from "@/components/CourseContent/styles/contentHero.module.css";
import ProgramPromoBanner from "../../ProgramPromoBanner";
import { getAllInstructorsFromCourse } from "@/lib/instructor/getAllInstructorsFromCourse";
import MidPageCTA from "@/components/ProgramDetailLayout/templates/NewDesign/components/MidPageCTA";
import ProgramHeader from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramHeader";
import getProgramDuration from "@/lib/program/utils/getProgramDuration";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import getProgramFromSubject from "@/components/ProgramDetailLayout/templates/utils/getSubjectFromProgram";
import CategoryCTA from "@/components/ProgramDetailLayout/templates/NewDesign/components/CategoryCTA";
import ProgramRelatedContent from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramRelatedContent";
import { CourseVideo } from "@/components/CourseContent/ContentOverview/types";
import ProgramEnroll from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramEnroll";
import IndustryInsights from "@/components/ProgramDetailLayout/templates/NewDesign/components/IndustryInsights";
import checkRTL from "@/utils/functions/checkRTL";
import sortBySortValue from "@/utils/functions/sortBySortValue";
import ProgramSummaryPathway from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramSummaryPathway";
import { SITE_URL } from "@/lib/program/utils/seo/generateProgramSeo";
import SocialShare from "@/components/SocialShare";
import ProgramEndorsements from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramEndorsements";
import OFACMessage from "@/components/CourseContent/ContentRefresh/OFACmessage";
import ProgramStickyCTA from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramStickyCTA";
import { useProgramRedesignConfig } from "@/monarch/rules/programRedesignTraffic/useProgramRedesignTraffic";
import getSortedComponentsBySectionOrder from "@/utils/getSortedComponentsBySectionOrder";
import {
  ProgramRedesignTrafficTestResult,
  SideRailComponentProps,
} from "@/monarch/rules/programRedesignTraffic/types";
import useSideRailComponentList from "@/components/SideRail/HeroSideRail/useSideRailComponentList";
import { RedesignedTrackComparison } from "@/components/Certificate/constants/enums";
import CertificateEmphasisTabs from "@/components/Certificate/CertificateEmphasisTabs";
import ProgramEnrollButton from "@/components/ProgramDetailLayout/templates/components/ProgramEnrollButton";
import SideRailMobileContent from "@/components/SideRail/HeroSideRail/SideRailContent/MobileContent";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import ProgramFAQs from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramFAQs";
import Image from "next/image";
import CountdownBanner from "@/components/CountdownBanner";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import HeroSideRailContainer from "@/components/SideRail/HeroSideRail/HeroSideRailContainer";
import TechMicrocredentialsCTA from "../components/TechMicrocredentialsCTA";
import BundleSavingsBanner from "@/components/BundleSavingsBanner/BundleSavingsBanner";
import { getProgramSearchUrl } from "@/components/CourseContent/templates/common/utils/getProgramSearchUrl";
import BenefitsModule from "@/components/BenefitsModule";
import PartnerInstructors from "@/components/PartnerInstructors";
import CareerCards from "@/components/CareerCards";
import {
  CAREER_CARDS_VARIANTS,
  resolveCareerCardSkills,
  getIndustryKey,
} from "@/components/CareerCards/constants";
import RelatedProgram from "@/components/CourseContent/templates/common/utils/renderRelatedProgram";
import { programDetailPageEventConfig } from "../../constants";
import { useGetIsSideRailShown } from "@/hooks/useGetIsSideRailShown";
import SocialProof from "@/components/SocialProof";
import { headerStylesInDetailPages } from "@/components/SocialProof/const";
import useClientEnrollments from "@/lib/partner-api/useClientEnrollments";
import useClientEntitlements from "@/lib/partner-api/useClientEntitlements";
import useEmailOptIn from "../context/ProgramDetailContext/hooks/useEmailOptIn";
import useEnrollmentUrl from "@/lib/program/utils/programEnroll/useEnrollmentUrl";
import { useProgramCompareCtaUrl } from "@/components/ProductCompare/useProgramCompareCtaUrl";
import useCertificateCTAText from "@/monarch/rules/certificateCTA/useCertificateCTAText";
import { getBrandString } from "@/utils/tagular/helpers";
import RecentlyViewedProductCardList from "@/components/RecentlyViewedProductCardList";
import ProductCompareWithPartnerPrograms from "@/components/ProductCompare/components/ProductCompareWithPartnerPrograms";
import { useDetailPricingData } from "@/lib/checkout-geo-pricing";
import { ProgramCurrencyBarSurface } from "@/components/ProgramDetailLayout/templates/components/ProgramCurrencySurfaces";
import useTranslations from "@/i18n/useTranslations";

function hasSideRailVideoPlayerComponent(components: SideRailComponentProps[]) {
  return !!components.find((componentItem) => {
    return componentItem.component === "videoPlayer";
  });
}

export default function ProgramDetailContent({
  programData,
  courses,
  pricingData: serverPricingData,
  skus,
  programIsEnrollable,
  programIsRetired = false,
  upsaleTechMicrocredsCourses,
  enrichedInstructors,
}: ProgramDetailContentProps) {
  const tProgram = useTranslations("Program");
  const tExpand = useTranslations("ExpandableBlock");
  const {
    geoPricing,
    sectionsOrder,
    headerCta,
    isLoading,
    factoidsBar,
    promoBanner,
    showSideRail,
    sideRailHeaderComponents,
    sideRailBodyComponents,
    sideRailCollapsedHeaderComponents,
    sideRailCollapsedBodyComponents,
    sideRailMobileHeaderComponents,
    sideRailMobileBodyComponents,
    hidePrice,
    emphasisModuleVersion,
    information,
    stats,
    testimonial,
    tabsOrder,
    pathInfo,
    sideRailImage,
    sideRailBulletList,
    sideRailTitle,
    sideRailProductTitle,
    sideRailRating,
    sideRailSkillsList,
    sideRailVideoPlayer,
    sideRailImageTitle,
    sideRailStatisticComponent,
    bundleSavingsBanner,
    benefitsModule,
    careerCards,
    expandableRelatedPrograms,
    productCompare,
    currencySelector,
    testimonialsModule,
  } = useProgramRedesignConfig();

  const pricingData = useDetailPricingData({
    pricingData: serverPricingData,
    geoPricing,
    product: {
      productType: programData.productType,
      partners: programData.partners?.map((partner) => ({ key: partner.key })),
    },
  });

  const sideRailComponentList = useSideRailComponentList({
    productData: programData,
    courses,
    pricingData,
    skus,
    programIsEnrollable,
    programIsRetired,
    sideRailImage,
    sideRailBulletList,
    sideRailTitle,
    sideRailImageTitle,
    sideRailStatisticComponent,
    currencySelector,
  });
  const { isSideRailVisible } = useGetIsSideRailShown(showSideRail);

  const { enrollments } = useClientEnrollments();
  const { entitlements } = useClientEntitlements();
  const { emailOptIn } = useEmailOptIn();

  const enrollmentUrl = useEnrollmentUrl(
    programData,
    courses,
    skus,
    enrollments,
    entitlements,
    emailOptIn,
  );
  const programCompareCtaUrl = useProgramCompareCtaUrl({
    programData,
    courses,
    skus,
    programIsEnrollable,
  });

  const { ctaText: enrollCtaLabel } = useCertificateCTAText({
    isProgramEnrollable: true,
  });

  //If the video is live in the side rail, then it shouldn't be in the content.
  const showingSideRailVideo =
    !!showSideRail &&
    hasSideRailVideoPlayerComponent(
      (sideRailHeaderComponents || [])
        .concat(sideRailBodyComponents || [])
        .concat(sideRailCollapsedHeaderComponents || [])
        .concat(sideRailCollapsedBodyComponents || [])
        .concat(sideRailMobileHeaderComponents || [])
        .concat(sideRailMobileBodyComponents || []),
    );

  const { expectedLearningItems } = programData;

  const programOverviewVideo =
    programData.video.length > 0
      ? (programData.video[0] as CourseVideo)
      : undefined;
  if (programOverviewVideo) {
    // Program videos do not have a thumbnail image, this one was provided as fallback by the design team
    programOverviewVideo.image = {
      src: "/bg-images/video-player-thumbnail.jpg",
    };
  }
  const programSubject = getProgramFromSubject(programData);
  programData.video.length > 0
    ? (programData.video[0] as CourseVideo)
    : undefined;
  const instructors = getAllInstructorsFromCourse(courses);

  const formattedInsights = programData.jobOutlookItems
    ?.filter((item) => item && item.value !== null)
    .map((item) => ({
      value: item.value ?? "",
    }));

  const duration = courses && getProgramDuration(courses);
  const filteredExpectedLearningItems = sortBySortValue(
    expectedLearningItems.filter((item) => item.value),
  );
  const areLearningItemsRTL = checkRTL(
    filteredExpectedLearningItems.map((item) => item.value).join(""),
  );

  const componentList = [
    {
      key: "promoBanner",
      renderer: () => {
        return promoBanner &&
          programIsEnrollable &&
          programData.programEligibleForOneClickPurchase ? (
          <ProgramPromoBanner
            courses={courses}
            programData={programData}
            skus={skus}
            data={promoBanner}
            sideRail={!!showSideRail}
          />
        ) : null;
      },
    },
    {
      key: "benefitsModule",
      renderer: () => {
        return benefitsModule ? (
          <BenefitsModule
            location="detail-page"
            schoolName={
              programData.partners?.length ? programData.partners[0].name : ""
            }
            benefitsModule={benefitsModule}
          />
        ) : null;
      },
    },
    {
      key: "whatYoullLearn",
      renderer: () => {
        return filteredExpectedLearningItems &&
          filteredExpectedLearningItems.length > 0 ? (
          <WhatYoullLearn
            key="whatYoullLearn"
            title={tProgram("whatYoullLearn")}
            learningItems={filteredExpectedLearningItems}
            areLearningItemsRTL={areLearningItemsRTL}
          />
        ) : null;
      },
    },
    {
      key: "programOverview",
      renderer: () => {
        return (
          <ProgramContent
            key="programOverview"
            title={tProgram("programOverview")}
            fullDescription={programData.overview}
            video={programOverviewVideo}
            isVideoInSideRail={programOverviewVideo && showingSideRailVideo} //video should not show when the side bar is displayed
          />
        );
      },
    },
    {
      key: "emphasisModuleVersion",
      renderer: () => {
        return emphasisModuleVersion &&
          emphasisModuleVersion !== RedesignedTrackComparison.Hidden &&
          programIsEnrollable &&
          programData.programEligibleForOneClickPurchase ? (
          <div className={maxWidthContainer}>
            {emphasisModuleVersion === RedesignedTrackComparison.Tabbed ? (
              <CertificateEmphasisTabs
                key="certificateEmphasisTabs"
                information={information}
                stats={stats}
                testimonial={testimonial}
                tabOrder={tabsOrder}
                hidePrice={hidePrice}
                pathInfo={pathInfo}
                ctaElementRenderer={(tabId: string, ctaClassName: string) => {
                  return (
                    <ProgramEnrollButton
                      programData={programData}
                      courses={courses}
                      skus={skus}
                      location={`cert-module-${tabId}`}
                      enableTracking={true}
                    />
                  );
                }}
              />
            ) : null}
          </div>
        ) : null;
      },
    },
    {
      key: "testimonialsModule", //redesign
      renderer: () => {
        return testimonialsModule ? (
          <SocialProof
            header={testimonialsModule.header.title}
            items={testimonialsModule.testimonials}
            linkText={testimonialsModule.header.ctaLinkText}
            linkUrl={testimonialsModule.header.ctaLinkUrl}
            eventConfig={programDetailPageEventConfig}
            headerTextClassname={headerStylesInDetailPages}
            isSideRailShown={isSideRailVisible}
            containerClassName=""
          />
        ) : null;
      },
    },
    {
      key: "programSummaryPathway",
      renderer: () => {
        return (
          <ProgramSummaryPathway
            key="programSummaryPathway"
            courses={courses}
          />
        );
      },
    },
    {
      key: "midPageCTA",
      renderer: () => {
        return programData.programEligibleForOneClickPurchase &&
          programIsEnrollable ? (
          <MidPageCTA
            key="midPageCTA"
            programData={programData}
            courses={courses}
            skus={skus}
            className={showSideRail ? "hidden md:flex xl:hidden" : ""}
          />
        ) : null;
      },
    },
    {
      key: "technicalMicrocredentialsCta",
      renderer: () => {
        return upsaleTechMicrocredsCourses ? (
          <TechMicrocredentialsCTA items={upsaleTechMicrocredsCourses} />
        ) : null;
      },
    },
    {
      key: "instructorList",
      renderer: () => {
        return (
          <InstructorList
            key="instructorList"
            instructors={instructors}
            showSingleColumn={showSideRail}
          />
        );
      },
    },
    {
      key: "featuredInstructor",
      renderer: () => {
        return enrichedInstructors ? (
          <PartnerInstructors
            key="featuredInstructor"
            instructors={enrichedInstructors}
          />
        ) : null;
      },
    },
    {
      key: "industryInsights",
      renderer: () => {
        return formattedInsights?.length > 0 ? (
          <IndustryInsights
            key="industryInsights"
            insights={formattedInsights}
          />
        ) : null;
      },
    },
    {
      key: "programEndorsements",
      renderer: () => {
        return programData.corporateEndorsements?.length > 0 ||
          programData.individualEndorsements?.length > 0 ? (
          <ProgramEndorsements
            key="programEndorsements"
            corporateEndorsements={programData.corporateEndorsements}
            individualEndorsements={programData.individualEndorsements}
          />
        ) : null;
      },
    },
    {
      key: "creditPathways",
      renderer: () => {
        return programData.creditRedemptionOverview ? (
          <ProgramContent
            key="creditPathways"
            title={tProgram("certificateCreditPathways")}
            fullDescription={programData.creditRedemptionOverview}
          />
        ) : null;
      },
    },
    {
      key: "programEnroll",
      renderer: () => {
        return programData.programEligibleForOneClickPurchase &&
          programIsEnrollable &&
          pricingData &&
          (pricingData.originalPrice || pricingData.discountedPrice) ? (
          <ProgramEnroll
            key="programEnroll"
            courses={courses || []}
            programData={programData}
            duration={duration || null}
            skus={skus}
            pricingData={pricingData}
          />
        ) : null;
      },
    },
    {
      key: "programRelatedContent",
      renderer: () => {
        return (
          <ProgramRelatedContent
            key="programRelatedContent"
            programSubject={programSubject}
          />
        );
      },
    },
    {
      key: "faqs",
      renderer: () => {
        return programData.faq && programData.faq.length > 0 ? (
          <ProgramFAQs key="faqs" faqs={programData.faq} />
        ) : null;
      },
    },
    {
      key: "recentlyViewedProductCardList",
      renderer: () => (
        <RecentlyViewedProductCardList
          currentProductId={programData.externalId || programData.objectID}
          showAllProducts={false}
          filterByPartner={programData.partners?.[0]?.name}
          displayLimit={4}
          minItemsToRender={2}
          surface="pdp"
          tagularPageType="Detail-Page"
        />
      ),
    },
    {
      key: "countdownBanner",
      renderer: () => {
        return (
          <FeatureFlagComponentResolverClient
            flagKey="NEXT_PUBLIC_FEATURE_OC_COUNTDOWN_BANNER"
            variants={{
              test: (
                <MonarchDataConsumer<ProgramRedesignTrafficTestResult>
                  experimentKey="useProgramRedesignTraffic"
                  render={(testResult) => {
                    const countdownBanner = testResult?.data?.countdownBanner;
                    return (
                      <CountdownBanner
                        variant="cta-compact"
                        theme={countdownBanner?.btf?.theme || "light"}
                        header={countdownBanner?.btf?.text || ""}
                        subHeader={countdownBanner?.btf?.subtext || ""}
                        showSubheader={
                          countdownBanner?.btf?.subtextEnabled ?? false
                        }
                        targetDate={countdownBanner?.btf?.date || ""}
                        htmlId="promo-countdown-banner"
                        location="program-detail-page"
                        name="promo-countdown-banner"
                        showCTA={
                          programIsEnrollable &&
                          programData.programEligibleForOneClickPurchase
                        }
                        ctaElement={
                          <ProgramEnrollButton
                            courses={courses}
                            programData={programData}
                            skus={skus}
                            buttonClass="w-full"
                            enableTracking={true}
                            ctaText={countdownBanner?.btf?.ctaText}
                          />
                        }
                        tooltip={countdownBanner?.btf?.tooltip}
                      />
                    );
                  }}
                />
              ),
              legacy: <></>,
            }}
            fallback="legacy"
          />
        );
      },
    },
    {
      key: "expandableRelatedPrograms",
      renderer: () => {
        if (!courses || courses.length === 0) return null;
        return (
          <RelatedProgram
            designTobeSelected="2"
            ignorePricingData={true}
            ignoreSubtitle={true}
            course={courses[0]}
            expandableSection={{
              title:
                expandableRelatedPrograms?.title ??
                tProgram("continueYourLearningJourney"),
              subtitle: expandableRelatedPrograms?.subtitle,
              reasons: expandableRelatedPrograms?.reasons,
              showMoreLabel:
                expandableRelatedPrograms?.showMoreLabel ??
                tProgram("showMorePrograms"),
              showLessLabel:
                expandableRelatedPrograms?.showLessLabel ?? tExpand("showLess"),
              backgroundColor:
                expandableRelatedPrograms?.backgroundColor ?? "putty",
              descriptionFeaturedProgramsAppend:
                expandableRelatedPrograms?.descriptionFeaturedProgramsAppend,
            }}
            cardType={expandableRelatedPrograms?.cardType ?? "program-card"}
          />
        );
      },
    },
    {
      key: "bundleSavingsBanner",
      renderer: () => {
        const ctaUrl =
          bundleSavingsBanner?.cta?.linkType === "search"
            ? getProgramSearchUrl(programData.partners)
            : enrollmentUrl;
        return bundleSavingsBanner ? (
          <BundleSavingsBanner
            key="bundleSavingsBanner"
            title={bundleSavingsBanner?.title}
            titleEmphasis={bundleSavingsBanner?.titleEmphasis}
            badge={bundleSavingsBanner?.badge}
            description={bundleSavingsBanner?.description}
            discount={bundleSavingsBanner?.discount}
            bulletPoints={bundleSavingsBanner?.bulletPoints}
            cta={{
              label:
                bundleSavingsBanner?.cta?.label ||
                tProgram("exploreMorePrograms"),
              url: ctaUrl,
              html_id: "bundling-cta",
              location: "program-detail-page",
              name: "program-bundling-CTA",
            }}
            partnerName={programData.partners?.[0]?.name}
            programData={programData}
            courses={courses}
            isEnrollmentUrl={ctaUrl === enrollmentUrl}
          />
        ) : null;
      },
    },
    {
      key: "careerCards",
      renderer: () => {
        const category = getIndustryKey({
          title: programData.title,
          subjectEn: programData.subjectEn || "",
          subjectEs: programData.subjectEs || "",
          overview: programData.overview,
          subjects: programData.subjects,
          skills: programData.skills,
        });
        const careerCardsData =
          CAREER_CARDS_VARIANTS[category || "dataScience"];
        return (
          <CareerCards
            location={careerCards?.location || "detail-page"}
            isSideRail={careerCards?.isSideRail || true}
            industry={careerCards?.industry || category || "dataScience"}
            variant={careerCards?.variantKey || "default"}
            heading={careerCards?.heading || ""}
            title={careerCards?.title || careerCardsData.heading}
            stats={(careerCards?.stats || careerCardsData.cards).slice()}
            skills={resolveCareerCardSkills(
              careerCards?.skills,
              programData.skills,
              category || "dataScience",
            ).slice()}
            hiddenCta={true}
          />
        );
      },
    },
    {
      key: "productCompare",
      renderer: () => {
        if (!productCompare) return null;
        const partnerNames = programData.partners.map((p) => p.name);
        return (
          <ProductCompareWithPartnerPrograms
            productCompare={productCompare}
            currentUuid={programData.objectID}
            partnerNames={partnerNames}
            product={{
              productUuid: programData.objectID,
              partners: programData.partners,
              subjects: programData.subjects,
              subjectEn: programData.subjectEn,
              subjectEs: programData.subjectEs,
              skills: programData.skills,
            }}
            currentCourseCheckoutUrl={programCompareCtaUrl}
            location="program-detail-page"
            hasStickyOffset={
              programData.programEligibleForOneClickPurchase &&
              programIsEnrollable
                ? "all"
                : undefined
            }
            currentCtaLabel={
              programIsEnrollable &&
              programData.programEligibleForOneClickPurchase
                ? enrollCtaLabel
                : undefined
            }
          />
        );
      },
    },
  ];

  const headerContainerClasses = showSideRail
    ? "xl:flex xl:gap-6 relative"
    : "";
  const bodyContainerClasses = showSideRail ? "xl:w-[1280px] xl:mx-auto" : "";

  return (
    <>
      <div
        className={`${
          headerCta?.bgColor !== "light"
            ? styles.noImageContentHero
            : "bg-putty-100"
        } pb-12 pt-20 sm:py-12 w-full`}
      >
        <Image
          className={`block w-full h-auto object-cover -z-10 absolute inset-0 ${styles.contentImage}`}
          src="/bg-images/learn-page-header.webp"
          alt=""
          fill
          priority
        />
        <div
          className={`${maxWidthContainer} ${fullWidthContainer} gap-8 z-10`}
        >
          <Breadcrumbs
            variant={headerCta?.bgColor === "light" ? "dark" : "light"}
            path={programData?.marketingUrl || ""}
            pageName={programData.title}
            containerClassName="not-prose"
            linksToShowOnSmallScreen={1}
          />
          <div className={headerContainerClasses}>
            <div
              className={`flex flex-col gap-8 ${showSideRail ? "xl:max-w-[824px]" : ""}`}
            >
              {showSideRail && (
                <div className="md:hidden block">
                  <SideRailMobileContent
                    componentList={sideRailComponentList}
                    sideRailMobileBodyComponents={sideRailMobileBodyComponents}
                    sideRailMobileHeaderComponents={
                      sideRailMobileHeaderComponents
                    }
                  />
                </div>
              )}
              <ProgramHeader
                pricingData={pricingData}
                duration={duration}
                programIsEnrollable={programIsEnrollable}
                programIsRetired={programIsRetired}
                skus={skus}
                courses={courses}
                programData={programData}
                showSideRail={showSideRail}
                variant={headerCta?.bgColor === "light" ? "light" : "dark"}
              />
              <div className="w-full" id="program-factoid-container">
                {courses && courses.length > 0 && (
                  <FactoidBar
                    courseData={courses}
                    isFactoidsBarFieldsLoading={isLoading}
                    monarchFactoidsBar={factoidsBar}
                    isHiddingLastElement={showSideRail}
                  />
                )}
              </div>
            </div>
            <div className="hidden xl:block">
              {showSideRail && (
                <>
                  <HeroSideRailContainer
                    productData={programData}
                    courses={courses}
                    pricingData={pricingData}
                    skus={skus}
                    programIsEnrollable={programIsEnrollable}
                    programIsRetired={programIsRetired}
                    sideRailTitle={sideRailTitle}
                    sideRailImage={sideRailImage}
                    sideRailBulletList={sideRailBulletList}
                    sideRailHeaderComponents={sideRailHeaderComponents}
                    sideRailBodyComponents={sideRailBodyComponents}
                    sideRailProductTitle={sideRailProductTitle}
                    sideRailCollapsedHeaderComponents={
                      sideRailCollapsedHeaderComponents
                    }
                    sideRailCollapsedBodyComponents={
                      sideRailCollapsedBodyComponents
                    }
                    sideRailRating={sideRailRating}
                    sideRailSkillsList={sideRailSkillsList}
                    sideRailVideoPlayer={sideRailVideoPlayer}
                    sideRailImageTitle={sideRailImageTitle}
                    sideRailStatisticComponent={sideRailStatisticComponent}
                    currencySelector={currencySelector}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <ProgramCurrencyBarSurface
        pricingData={pricingData}
        programIsEnrollable={programIsEnrollable}
        programIsRetired={programIsRetired}
      />
      {programData.programEligibleForOneClickPurchase && programIsEnrollable ? (
        <ProgramStickyCTA
          programData={programData}
          courses={courses}
          pricingData={pricingData}
          skus={skus}
          className={showSideRail ? "xl:hidden" : ""}
        ></ProgramStickyCTA>
      ) : null}
      <div
        className="bg-putty-100 pt-12 md:pt-16 flex flex-col gap-12 sm:gap-16"
        id="program-details-body-container"
      >
        <div className={bodyContainerClasses}>
          <div className={showSideRail ? mainContentSideRail : mainContent}>
            {/* PROMO BANNER GOES HERE */}
            {getSortedComponentsBySectionOrder({
              componentList,
              sectionsOrder,
            })}
          </div>
        </div>
        {programData.hasOfacRestrictions && (
          <OFACMessage type="program" sideRail={showSideRail} />
        )}
        <div>
          <SocialShare
            title={tProgram("shareThisProgram")}
            productUrl={`${SITE_URL.replace(/\/$/, "")}/${programData.marketingUrl.replace(/^\//, "")}`}
            productName={programData.title}
          />
        </div>
        <div
          className={`bg-putty-300 w-full flex flex-col lg:h-64 justify-center items-center`}
          id="program-category-cta"
        >
          <div className={maxWidthContainer}>
            <CategoryCTA programSubject={programSubject} />
          </div>
        </div>
      </div>
    </>
  );
}
